import { useId } from "react";
import { usePersonalReduceMotion, writeReduceMotionPreference } from "./motionPreference";

/** The person whose motion choice applies: the sign-in name (the same identity the Gantt uses for remembered filters). */
export function motionPersonOf(principal: { userPrincipalName?: string | null; displayName?: string | null } | null | undefined): string | null {
  return principal?.userPrincipalName ?? principal?.displayName ?? null;
}

/**
 * "Reduce motion": a personal switch (My profile) that turns off moving dots, sliding panels, glows and number tweens on
 * every page, for this person in this browser. The device's own reduced-motion setting is always honoured as well.
 */
export function ReduceMotionSetting({ person }: { person: string | null }) {
  const on = usePersonalReduceMotion();
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4" data-testid="reduce-motion-setting">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-slate-900">
          Reduce motion
        </label>
        <p className="mt-0.5 text-xs text-slate-600" id={`${id}-help`}>
          Stops moving dots, sliding panels, glows and counting numbers. Figures and colours stay the same. Your computer’s own “reduce motion” setting is
          always followed too.
        </p>
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={on}
        aria-describedby={`${id}-help`}
        onClick={() => writeReduceMotionPreference(person, !on)}
        className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 ${
          on ? "border-brand-600 bg-brand-600" : "border-slate-300 bg-slate-200"
        }`}
        data-testid="reduce-motion-switch"
      >
        <span
          aria-hidden="true"
          className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`}
        />
      </button>
    </div>
  );
}
